import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import AppointmentFormDialog from "components/Modals/AppointmentFormDialog";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { usePaginatedSearch } from "hooks/usePaginatedSearch";
import { useEffect, useMemo, useState } from "react";
import { FaPlusCircle } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getPaginatedClientData, useClientById } from "utils/clientService";
import AppointmentDetailDialog from "./AppointmentDetailDialog";
import AppointmentDeleteDialog from "./AppointmentDeleteDialog";
import AppointmentsCalendar from "./AppointmentsCalendar";
import { invalidateEmailLogQueries } from "./appointmentNotifyUtils";
import {
  APPOINTMENT_STATUSES,
  APPOINTMENT_VISIBILITY,
  appointmentMatchesClients,
} from "./appointmentUtils";

const fetchClientPage = ({ page, size, search }) =>
  getPaginatedClientData({ pageIndex: page, pageSize: size, searchText: search });

const EMPTY_FILTERS = {
  search: "",
  createdById: null,
  withUserId: null,
  clientId: null,
  startDate: "",
  endDate: "",
  visibility: "",
};

/**
 * @param {object} props
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.embedded]
 * @param {number[]|null} [props.restrictToClientIds] When set, only appointments for these
 *   clients are shown (partner tenancy). Pass `[]` for “no clients yet”.
 * @param {{id:number,label:string}[]} [props.scopedClientOptions] Client filter options when restricted.
 */
const Appointments = ({
  readOnly = false,
  embedded = false,
  restrictToClientIds = null,
  scopedClientOptions = null,
}) => {
  const queryClient = useQueryClient();
  const [{ usersList, userDetail }] = useKothar();
  const isClientScoped = Array.isArray(restrictToClientIds);

  const [statusFilter, setStatusFilter] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState(EMPTY_FILTERS.search);
  const [mineActive, setMineActive] = useState(!isClientScoped);

  const {
    inputValue: clientSearchInput,
    setInputValue: setClientSearchInput,
    options: clientSearchResults,
    loading: clientSearchLoading,
    listboxProps: clientListboxProps,
  } = usePaginatedSearch(fetchClientPage, { pageSize: 20 });
  const { data: selectedClientForFilter } = useClientById(filters.clientId);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 350);
    return () => clearTimeout(timer);
  }, [filters.search]);
  const [reminderApptId, setReminderApptId] = useState(null);
  const [openAddAppointmentModal, setOpenAddAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // A notification can open one appointment directly (e.g. one a referral partner booked).
  const location = useLocation();
  const navigate = useNavigate();
  const linkedAppointmentId = embedded ? null : location.state?.appointmentId;
  const linkedAppointment = useQuery({
    queryKey: ["appointment", linkedAppointmentId],
    queryFn: async () => (await axios.get(`${API_URL}/appointments/${linkedAppointmentId}`)).data,
    enabled: Boolean(linkedAppointmentId),
    retry: false,
  });
  useEffect(() => {
    if (!linkedAppointmentId) return;
    if (linkedAppointment.data) {
      setSelectedAppointment(linkedAppointment.data?.data ?? linkedAppointment.data);
    } else if (linkedAppointment.isError) {
      toast.error("That appointment is no longer available.");
    } else {
      return;
    }
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedAppointment.data, linkedAppointment.isError]);

  const userOptions = useMemo(
    () => (usersList ?? []).map((u) => ({ id: u.id, label: u.name || u.email || `User #${u.id}` })),
    [usersList]
  );

  const clientOptions = useMemo(() => {
    if (isClientScoped && Array.isArray(scopedClientOptions)) {
      return scopedClientOptions;
    }
    const fromSearch = (clientSearchResults ?? []).map((c) => ({
      id: c.id,
      label: c.name || c.email || `Client #${c.id}`,
    }));
    if (!selectedClientForFilter?.id) return fromSearch;
    if (fromSearch.some((c) => c.id === selectedClientForFilter.id)) return fromSearch;
    return [
      {
        id: selectedClientForFilter.id,
        label:
          selectedClientForFilter.name ||
          selectedClientForFilter.email ||
          `Client #${selectedClientForFilter.id}`,
      },
      ...fromSearch,
    ];
  }, [clientSearchResults, selectedClientForFilter, isClientScoped, scopedClientOptions]);

  const allowedClientIdSet = useMemo(() => {
    if (!isClientScoped) return null;
    return new Set(restrictToClientIds.map(Number).filter((id) => Number.isFinite(id)));
  }, [isClientScoped, restrictToClientIds]);

  const hasActiveFilters =
    statusFilter !== "" ||
    filters.search !== "" ||
    (!isClientScoped && filters.createdById != null) ||
    (!isClientScoped && filters.withUserId != null) ||
    filters.clientId != null ||
    filters.startDate !== "" ||
    filters.endDate !== "" ||
    filters.visibility !== "" ||
    (!isClientScoped && !mineActive);

  const { data, isFetching, refetch } = useQuery({
    queryKey: [
      "all-appointments",
      statusFilter,
      debouncedSearch,
      filters.createdById,
      filters.withUserId,
      filters.clientId,
      filters.startDate,
      filters.endDate,
      filters.visibility,
      isClientScoped ? false : mineActive,
      isClientScoped ? null : userDetail?.id,
      isClientScoped ? [...(restrictToClientIds || [])].sort() : null,
    ],
    queryFn: async () => {
      // Partner with zero clients: never load the org-wide calendar.
      if (isClientScoped && (!restrictToClientIds || restrictToClientIds.length === 0)) {
        return [];
      }

      const params = new URLSearchParams({ page: 0, size: 500 });
      if (statusFilter) params.set("status", statusFilter);
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());

      if (!isClientScoped) {
        if (mineActive && userDetail?.id != null) {
          params.set("involvedUserId", String(userDetail.id));
        } else {
          if (filters.createdById != null) params.set("createdById", String(filters.createdById));
          if (filters.withUserId != null) params.set("withUserId", String(filters.withUserId));
        }
      }

      if (filters.clientId != null) {
        if (!allowedClientIdSet || allowedClientIdSet.has(Number(filters.clientId))) {
          params.set("clientId", String(filters.clientId));
        }
      }
      if (filters.startDate) params.set("startDate", filters.startDate);
      if (filters.endDate) params.set("endDate", filters.endDate);
      if (filters.visibility) params.set("visibility", filters.visibility);

      const rows = await axios
        .get(`${API_URL}/appointments?${params}`)
        .then((r) => r.data?.data?.content ?? []);

      if (!isClientScoped) return rows;
      return rows.filter((appt) => appointmentMatchesClients(appt, restrictToClientIds));
    },
    enabled: isClientScoped ? true : !mineActive || userDetail?.id != null,
    refetchOnWindowFocus: false,
  });

  const appointments = data ?? [];

  const { mutate: remove, isLoading: isDeleting } = useMutation({
    mutationFn: ({ id, sendEmail }) =>
      axios.delete(`${API_URL}/appointments/${id}`, { params: { sendEmail } }),
    onSuccess: () => {
      toast.success("Appointment deleted");
      setSelectedAppointment(null);
      setDeleteTarget(null);
      refetch();
      queryClient.invalidateQueries(["appointment"]);
      invalidateEmailLogQueries(queryClient);
    },
    onError: () => toast.error("Failed to delete appointment"),
  });

  const { mutate: sendReminder, isLoading: isSendingReminder } = useMutation({
    mutationFn: (apptId) => axios.post(`${API_URL}/appointments/${apptId}/send-reminder`),
    onSuccess: () => {
      toast.success("Reminder sent");
      setReminderApptId(null);
      refetch();
      invalidateEmailLogQueries(queryClient);
    },
    onError: () => toast.error("Failed to send reminder"),
  });

  const handleEditFromDetail = () => {
    setEditingAppointment(selectedAppointment);
    setSelectedAppointment(null);
  };

  const handleSendReminderClick = (apptId) => {
    setReminderApptId(apptId);
  };

  const setFilter = (field) => (value) => setFilters((prev) => ({ ...prev, [field]: value }));

  const clearFilters = () => {
    setStatusFilter("");
    setFilters(EMPTY_FILTERS);
    setMineActive(false);
  };

  const handleCreatedByChange = (userId) => {
    setMineActive(false);
    setFilter("createdById")(userId);
  };

  const handleWithUserChange = (userId) => {
    setMineActive(false);
    setFilter("withUserId")(userId);
  };

  const toggleMine = () => {
    setMineActive((prev) => {
      const next = !prev;
      if (next) {
        setFilters((f) => ({ ...f, createdById: null, withUserId: null }));
      }
      return next;
    });
  };

  return (
    <div className={embedded ? "w-full" : "flex flex-col min-h-screen"}>
      <div className={embedded ? "w-full" : "w-full px-4 py-4"}>
        <div className="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded bg-app-surface text-app-text">
          <Box className="flex flex-wrap justify-between items-center gap-3 mb-0 p-4">
            <Typography variant="h5" className="font-bold">
              Appointments ({appointments.length}){" "}
              <Tooltip title="Refresh">
                <IconButton onClick={() => refetch()} disabled={isFetching}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Typography>
            {!readOnly && (
              <div className="flex gap-2 items-center">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FaPlusCircle />}
                  onClick={() => setOpenAddAppointmentModal(true)}
                >
                  Add Appointment
                </Button>
              </div>
            )}
          </Box>

          <Box sx={{ px: 2, pb: 2 }}>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Chip
                  label="All"
                  size="small"
                  clickable
                  color={statusFilter === "" ? "primary" : "default"}
                  variant={statusFilter === "" ? "filled" : "outlined"}
                  onClick={() => setStatusFilter("")}
                />
                {APPOINTMENT_STATUSES.map((s) => (
                  <Chip
                    key={s}
                    label={s.charAt(0) + s.slice(1).toLowerCase()}
                    size="small"
                    clickable
                    color={statusFilter === s ? "primary" : "default"}
                    variant={statusFilter === s ? "filled" : "outlined"}
                    onClick={() => setStatusFilter(s)}
                  />
                ))}
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                flexWrap="wrap"
                useFlexGap
              >
                <TextField
                  label="Search"
                  size="small"
                  value={filters.search}
                  onChange={(e) => setFilter("search")(e.target.value)}
                  sx={{ minWidth: { xs: "100%", md: 180 }, flex: { md: "1 1 180px" } }}
                />
                {!readOnly && (
                  <Autocomplete
                    size="small"
                    options={userOptions}
                    getOptionLabel={(o) => o.label}
                    value={userOptions.find((u) => u.id === filters.createdById) ?? null}
                    onChange={(_, val) => handleCreatedByChange(val?.id ?? null)}
                    disabled={mineActive}
                    renderInput={(params) => <TextField {...params} label="Created by" />}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    sx={{ minWidth: { xs: "100%", md: 200 }, flex: { md: "1 1 200px" } }}
                  />
                )}{" "}
                {!readOnly && (
                  <Button
                    size="small"
                    variant={mineActive ? "contained" : "outlined"}
                    onClick={toggleMine}
                    disabled={!userDetail?.id}
                    sx={{ alignSelf: { xs: "flex-start", md: "center" }, height: 40 }}
                  >
                    Mine
                  </Button>
                )}
                <Autocomplete
                  size="small"
                  options={userOptions}
                  getOptionLabel={(o) => o.label}
                  value={userOptions.find((u) => u.id === filters.withUserId) ?? null}
                  onChange={(_, val) => handleWithUserChange(val?.id ?? null)}
                  disabled={mineActive}
                  renderInput={(params) => <TextField {...params} label="With" />}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  sx={{ minWidth: { xs: "100%", md: 200 }, flex: { md: "1 1 200px" } }}
                />
                <Autocomplete
                  size="small"
                  options={clientOptions}
                  loading={clientSearchLoading}
                  filterOptions={(x) => x}
                  inputValue={clientSearchInput}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "reset") return;
                    setClientSearchInput(newInputValue);
                  }}
                  getOptionLabel={(o) => o.label}
                  value={clientOptions.find((c) => c.id === filters.clientId) ?? null}
                  onChange={(_, val) => setFilter("clientId")(val?.id ?? null)}
                  renderInput={(params) => <TextField {...params} label="Client" />}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  noOptionsText={clientSearchLoading ? "Searching…" : "Type to search clients"}
                  ListboxProps={{ ...clientListboxProps }}
                  sx={{ minWidth: { xs: "100%", md: 200 }, flex: { md: "1 1 200px" } }}
                />
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={filters.startDate}
                  onChange={(e) => setFilter("startDate")(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: "100%", md: 150 } }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={filters.endDate}
                  onChange={(e) => setFilter("endDate")(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: "100%", md: 150 } }}
                />
                <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 140 } }}>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={filters.visibility}
                    label="Visibility"
                    onChange={(e) => setFilter("visibility")(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={APPOINTMENT_VISIBILITY.ORG}>Shared</MenuItem>
                    <MenuItem value={APPOINTMENT_VISIBILITY.PRIVATE}>Private</MenuItem>
                  </Select>
                </FormControl>
                {hasActiveFilters && (
                  <Button size="small" onClick={clearFilters} sx={{ alignSelf: "center" }}>
                    Clear filters
                  </Button>
                )}
              </Stack>
            </Stack>

            <AppointmentsCalendar
              appointments={appointments}
              isLoading={isFetching}
              onSelectAppointment={setSelectedAppointment}
            />
          </Box>
        </div>
      </div>

      {!readOnly && (
        <AppointmentFormDialog
          open={openAddAppointmentModal}
          onClose={() => setOpenAddAppointmentModal(false)}
          onSaved={() => {
            refetch();
            queryClient.invalidateQueries(["appointment"]);
          }}
        />
      )}

      {!readOnly && (
        <AppointmentFormDialog
          open={!!editingAppointment}
          onClose={() => setEditingAppointment(null)}
          appointment={editingAppointment}
          clientId={editingAppointment?.clientId ?? null}
          onSaved={() => {
            setEditingAppointment(null);
            refetch();
            queryClient.invalidateQueries(["appointment"]);
          }}
        />
      )}

      <AppointmentDetailDialog
        open={!!selectedAppointment}
        appointment={selectedAppointment}
        onClose={() => setSelectedAppointment(null)}
        onEdit={readOnly ? undefined : handleEditFromDetail}
        onDeleteRequest={readOnly ? undefined : setDeleteTarget}
        onSendReminder={readOnly ? undefined : handleSendReminderClick}
        isSendingReminder={isSendingReminder}
      />

      {!readOnly && (
        <AppointmentDeleteDialog
          open={!!deleteTarget}
          appointment={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          isDeleting={isDeleting}
          onConfirm={({ sendEmail }) => remove({ id: deleteTarget.id, sendEmail })}
        />
      )}

      {!readOnly && (
        <Dialog
          open={!!reminderApptId}
          onClose={() => setReminderApptId(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Send Reminder</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Send the appointment reminder email to the client using the default Appointment
              Reminder template?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReminderApptId(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={isSendingReminder}
              onClick={() => sendReminder(reminderApptId)}
            >
              Send
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default Appointments;
